import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AgGridReact } from 'ag-grid-react';
import { formatISO, isValid, parseISO } from 'date-fns';
import { useSearchParams } from 'react-router-dom';

import { SemenDto } from '@/services/semen-service';
import { Sire } from '@/types.gql';

import { SemenFilters } from '../util/types';

export type SemenAppContextType = {
  selectedSemen: SemenDto[];
  selectedSemenRef: React.RefObject<SemenDto[]>;
  semenData: SemenDto[] | null;
  filters: SemenFilters;
  isLoading: boolean;
  error: string | null;
  setSelectedSemen: (semen: SemenDto[]) => void;
  setSemenData: (data: SemenDto[] | null) => void;
  shippingSemen: SemenDto[];
  setShippingSemen: (semen: SemenDto[]) => void;
  addToShippingSemen: (semen: SemenDto[]) => void;
  clearShippingSemen: () => void;
  setFilters: (filters: SemenFilters) => void;
  updateFilter: (
    key: keyof SemenFilters,
    value: SemenFilters[keyof SemenFilters],
  ) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearFilters: () => void;
  resetState: () => void;
  hasUnsavedChanges: boolean;
  clearUnsavedChanges: () => void;
  currentSire: Sire | null;
  setCurrentSire: (sire: Sire | null) => void;
  homeTabGridRef: React.RefObject<AgGridReact> | null;
  refreshHomeTabGrid: () => void;
};

const SemenAppContext = createContext<SemenAppContextType | undefined>(
  undefined,
);

export const SemenAppProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedSemen, setSelectedSemen] = useState<SemenDto[]>([]);
  const [semenData, setSemenData] = useState<SemenDto[] | null>(null);
  const [shippingSemen, setShippingSemen] = useState<SemenDto[]>([]);
  const [filters, setFilters] = useState<SemenFilters>({});
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSire, setCurrentSire] = useState<Sire | null>(null);
  const homeTabGridRef = useRef<AgGridReact>(null);
  const selectedSemenRef = useRef<SemenDto[]>([]);

  const wrappedSetSelectedSemen = useCallback((semen: SemenDto[]) => {
    selectedSemenRef.current = semen;
    setSelectedSemen(semen);
  }, []);
  const refreshHomeTabGrid = useCallback(() => {
    if (!homeTabGridRef.current?.api) return;
    homeTabGridRef.current.api.refreshServerSide({ purge: true });
  }, []);

  useEffect(() => {
    const rDateParam = searchParams.get('rDate');

    const initialFilters: SemenFilters = {};

    if (rDateParam) {
      const rDate = parseISO(rDateParam);
      if (isValid(rDate)) {
        initialFilters.dateReceived = rDate;
      }
    }

    if (Object.keys(initialFilters).length > 0) {
      setFilters(initialFilters);
    }
  }, [searchParams]);

  const updateFilter = useCallback(
    (key: keyof SemenFilters, value: SemenFilters[keyof SemenFilters]) => {
      setFilters((prev) => {
        const newFilters = {
          ...prev,
          [key]: value,
        };

        const newSearchParams = new URLSearchParams(searchParams);

        if (key === 'dateReceived') {
          if (value) {
            newSearchParams.set(
              'rDate',
              formatISO(value as Date, { representation: 'date' }),
            );
          } else {
            newSearchParams.delete('rDate');
          }
        }

        setSearchParams(newSearchParams);
        return newFilters;
      });
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setFilters({});
    setSemenData([]);

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('rDate');
    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams]);

  const resetState = useCallback(() => {
    setSelectedSemen([]);
    selectedSemenRef.current = [];
    setSemenData(null);
    setFilters({});
    setLoading(false);
    setError(null);
  }, []);

  const addToShippingSemen = useCallback((semen: SemenDto[]) => {
    setShippingSemen((prev) => {
      const existingIds = new Set(prev.map((s) => String(s.id)));
      const toAdd = semen.filter((s) => !existingIds.has(String(s.id)));
      return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
    });
  }, []);

  const clearShippingSemen = useCallback(() => {
    setShippingSemen([]);
  }, []);

  const hasUnsavedChanges = useMemo(
    () => selectedSemen.length > 0 || shippingSemen.length > 0,
    [selectedSemen, shippingSemen],
  );

  const clearUnsavedChanges = useCallback(() => {
    selectedSemenRef.current = [];
    setSelectedSemen([]);
    setShippingSemen([]);
  }, []);

  const contextValue = useMemo<SemenAppContextType>(
    () => ({
      selectedSemen,
      selectedSemenRef,
      semenData,
      filters,
      isLoading,
      error,
      setSelectedSemen: wrappedSetSelectedSemen,
      setSemenData,
      shippingSemen,
      setShippingSemen,
      addToShippingSemen,
      clearShippingSemen,
      setFilters,
      updateFilter,
      setLoading,
      setError,
      clearFilters,
      resetState,
      hasUnsavedChanges,
      clearUnsavedChanges,
      currentSire,
      setCurrentSire,
      homeTabGridRef,
      refreshHomeTabGrid,
    }),
    [
      selectedSemen,
      semenData,
      shippingSemen,
      addToShippingSemen,
      clearShippingSemen,
      filters,
      isLoading,
      error,
      updateFilter,
      wrappedSetSelectedSemen,
      clearFilters,
      resetState,
      hasUnsavedChanges,
      clearUnsavedChanges,
      currentSire,
      refreshHomeTabGrid,
    ],
  );

  return (
    <SemenAppContext.Provider value={contextValue}>
      {children}
    </SemenAppContext.Provider>
  );
};

export const useSemenAppContext = () => {
  const context = useContext(SemenAppContext);
  if (context === undefined) {
    throw new Error('useSemenApp must be used within a SemenAppProvider');
  }
  return context;
};
